/**
 * AnalyticsConsent — small, GDPR-conscious consent banner.
 *
 * Rules encoded here:
 *  - Banner only appears until the user makes a choice (granted or declined).
 *  - PostHog is NOT initialised before choice is made (see analytics.js gate).
 *  - Choice persists in localStorage. Users can change it later from Settings.
 *  - Declining does NOT affect any core app functionality — all API calls,
 *    reminders, subscriptions, journals, visualisations continue to work.
 *  - Neither button auto-forwards anything to third parties; only the "Allow"
 *    button will subsequently initialise PostHog.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAnalyticsConsent, setAnalyticsConsent } from '@/lib/analytics';

// Legal / support routes are reachable without authentication and are used
// by App Store reviewers. Suppress the consent banner there so it can't
// overlay footer content when a reviewer scrolls to the bottom.
const SUPPRESS_ROUTES = ['/privacy', '/terms', '/support', '/guidelines'];

export const AnalyticsConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Only show when the user has NOT made a choice yet.
    if (getAnalyticsConsent() === null) {
      setVisible(true);
    }
    // Re-check if another tab / Settings changes the value while this mount is alive.
    const onStorage = (e) => {
      if (e.key === 'fs_analytics_consent') {
        setVisible(getAnalyticsConsent() === null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const decide = (choice) => {
    setAnalyticsConsent(choice);
    setVisible(false);
  };

  if (!visible) return null;
  if (SUPPRESS_ROUTES.includes(location.pathname)) return null;

  return (
    <div
      data-testid="analytics-consent-banner"
      role="dialog"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-[#1C1C1E]/95 px-4 py-4 backdrop-blur-md sm:px-6"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-relaxed text-[#D1C8BF]">
          Future Self uses optional analytics (PostHog EU) to understand how the app is used and make it better. We do not collect your name, email, journal entries or affirmations. You can change this any time in Settings.
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            data-testid="analytics-consent-decline"
            onClick={() => decide('declined')}
            className="rounded-full border border-white/15 px-4 py-2 text-sm text-[#D1C8BF] transition hover:bg-white/5"
          >
            Decline
          </button>
          <button
            type="button"
            data-testid="analytics-consent-allow"
            onClick={() => decide('granted')}
            className="rounded-full bg-[#8BAA9E] px-4 py-2 text-sm font-medium text-[#1C1C1E] transition hover:bg-[#7fa094]"
          >
            Allow
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsConsentBanner;
